import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { DEFAULT_PLATFORM, PLATFORMS, PLATFORM_IDS } from "@/data/platform";

const PlatformContext = createContext(null);

const STORAGE_KEY = "tracksy-platform";

export const PlatformProvider = ({ children }) => {
  const [platform, setPlatformState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_PLATFORM;
    const saved = window.localStorage?.getItem(STORAGE_KEY);
    return saved && PLATFORM_IDS.includes(saved) ? saved : DEFAULT_PLATFORM;
  });

  // Mount guard — only fire analytics after a real user-initiated change.
  const hasMounted = useRef(false);

  // Push the platform's accent into CSS vars + body attribute so every
  // component can theme itself via var(--brand-accent) etc.
  useEffect(() => {
    const p = PLATFORMS[platform] || PLATFORMS[DEFAULT_PLATFORM];
    const root = document.documentElement;
    root.style.setProperty("--brand-accent", p.accent);
    root.style.setProperty("--brand-accent-hover", p.accentHover);
    root.style.setProperty("--brand-accent-soft", p.accentSoft);
    root.style.setProperty("--brand-accent-ring", p.accentRing);
    root.style.setProperty("--brand-accent-glow", p.accentGlow);
    root.style.setProperty("--page-tint", p.pageTint);
    document.body.setAttribute("data-platform", platform);
    window.localStorage?.setItem(STORAGE_KEY, platform);
    // Fire an event so analytics / Meta Pixel can hear it
    window.dispatchEvent(new CustomEvent("tracksy:platform", { detail: platform }));

    // Analytics — only on genuine switches, not the initial hydration.
    if (hasMounted.current) {
      const payload = {
        platform,
        platform_name: p.short,
        platform_network: p.network,
      };
      try {
        if (typeof window.fbq === "function") {
          window.fbq("trackCustom", "PlatformSelected", payload);
        }
        if (typeof window.gtag === "function") {
          window.gtag("event", "platform_select", payload);
        }
        if (window.dataLayer && typeof window.dataLayer.push === "function") {
          window.dataLayer.push({ event: "platform_select", ...payload });
        }
      } catch (_) { /* analytics are best-effort */ }
    } else {
      hasMounted.current = true;
    }
  }, [platform]);

  const setPlatform = useCallback((id) => {
    if (PLATFORM_IDS.includes(id)) setPlatformState(id);
  }, []);

  const toggle = useCallback(() => {
    setPlatformState((prev) => (prev === "apple" ? "android" : "apple"));
  }, []);

  const config = PLATFORMS[platform] || PLATFORMS[DEFAULT_PLATFORM];

  return (
    <PlatformContext.Provider value={{ platform, config, setPlatform, toggle }}>
      {children}
    </PlatformContext.Provider>
  );
};

export const usePlatform = () => {
  const ctx = useContext(PlatformContext);
  if (!ctx) {
    // Safe fallback so components work outside the provider (e.g. tests)
    return { platform: DEFAULT_PLATFORM, config: PLATFORMS[DEFAULT_PLATFORM], setPlatform: () => {}, toggle: () => {} };
  }
  return ctx;
};
